import {PersonIcon} from '@primer/octicons-react'
import {Fragment} from 'react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {AssignedEvent$key} from './__generated__/AssignedEvent.graphql'
import type {UnassignedEvent$key} from './__generated__/UnassignedEvent.graphql'
import styles from './assignees.module.css'
import {AssignmentEventAssignee, type AssignmentEventAssigneeFragment} from './AssignmentEventAssignee'
import {RolledupAssignedEvent} from './RolledupAssignedEvent'
import {TimelineRow} from './row/TimelineRow'

type UnassignedEventProps = {
  queryRef: UnassignedEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: Record<string, Array<AssignedEvent$key | AssignedEvent$key>>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const UnassignedEventFragment = graphql`
  fragment UnassignedEvent on UnassignedEvent {
    databaseId
    createdAt
    actor {
      avatarUrl(size: 64)
      login
      profileResourcePath
      __typename
      ... on Bot {
        isCopilot
        isAgent
        displayName
      }
      ... on User {
        name
      }
      ... on EnterpriseUserAccount {
        name
      }
      ... on Mannequin {
        name
      }
    }
    assignee {
      ... on Actor {
        login
      }
      ... on User {
        __typename
        login
        resourcePath
        name
      }
      ... on Mannequin {
        __typename
        login
        resourcePath
        name
      }
      ... on Organization {
        __typename
        login
        resourcePath
      }
      ... on Bot {
        __typename
        login
        resourcePath
        isCopilot
        displayName
        isAgent
      }
    }
  }
`

export function UnassignedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: UnassignedEventProps): React.ReactElement {
  const {actor, createdAt, assignee, databaseId} = useFragment(UnassignedEventFragment, queryRef)
  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PersonIcon}
    >
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupAssignedEvent rollupGroup={rollupGroup} />
        ) : (
          <RemovedAssigneesRendering
            queryRefs={[queryRef]}
            selfAssigned={actor?.login === assignee?.login}
            rollup={false}
          />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const RemovedAssigneesRendering = ({
  queryRefs,
  selfAssigned,
  rollup,
}: {
  queryRefs: UnassignedEvent$key[]
  selfAssigned: boolean
  rollup: boolean
}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {!selfAssigned && `${LABELS.timeline.unassigned} `}
      {queryRefs.map((queryRef, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Fragment key={index}>
          <InternalRemovedAssigneesRendering
            queryRef={queryRef}
            rollup={rollup}
            first={index === 0}
            last={index === queryRefs.length - 1}
          />
        </Fragment>
      ))}
    </>
  )
}

const InternalRemovedAssigneesRendering = ({
  queryRef,
  rollup,
  first,
  last,
}: Pick<UnassignedEventProps, 'queryRef'> & {
  rollup: boolean
  first: boolean
  last: boolean
}) => {
  const {assignee, actor} = useFragment(UnassignedEventFragment, queryRef)

  if (!assignee?.login) {
    return null
  }

  return (
    <div className={styles.assigneeEventContainer}>
      {actor?.login === assignee?.login && !rollup ? (
        LABELS.timeline.removedTheirAssignment
      ) : (
        <>
          {!first && !last && <div className={styles.assigneeMarginRight}>,</div>}
          {!first && last && <div className={styles.assigneeMarginHorizontal}>{LABELS.timeline.and}</div>}
          {/* RELAY-CLEANUP(UnassignedEvent): boundary cast; remove when UnassignedEvent leaves Relay. */}
          <AssignmentEventAssignee assignee={assignee as AssignmentEventAssigneeFragment} />
        </>
      )}
    </div>
  )
}
